import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatCard, Tag, statusTone, Loader, Empty } from "@/components/kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft, Users, CheckCircle2, Activity, AlertTriangle, QrCode, Zap,
  MapPin, Radio, RefreshCw, ClipboardList,
} from "lucide-react";

export default function TournamentDetail() {
  const { tid } = useParams();
  const [cc, setCc] = useState(null);
  const [roster, setRoster] = useState(null);
  const [repl, setRepl] = useState(null);

  const loadCC = () => api.get(`/tournaments/${tid}/command-center`).then((r) => setCc(r.data)).catch(() => setCc(false));
  const loadRoster = () => api.get(`/tournaments/${tid}/roster`).then((r) => setRoster(r.data)).catch(() => {});
  useEffect(() => { loadCC(); loadRoster(); }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkin = async (aid) => {
    try { await api.post(`/assignments/${aid}/checkin`, { method: "QR" }); toast.success("Checked in"); loadCC(); loadRoster(); }
    catch { toast.error("Check-in failed"); }
  };
  const NEXT = { invited: "accepted", accepted: "confirmed", confirmed: "scheduled", scheduled: "checked_in", checked_in: "in_progress", in_progress: "completed", completed: "approved", approved: "paid" };
  const advance = async (a) => {
    const next = NEXT[a.status];
    if (!next) return;
    try { await api.put(`/assignments/${a.id}/status`, { status: next }); toast.success(`Marked ${next.replace("_", " ")}`); loadCC(); loadRoster(); }
    catch { toast.error("Update failed"); }
  };
  const findReplacements = async (role) => {
    setRepl({ role, loading: true });
    try { const { data } = await api.get(`/tournaments/${tid}/replacements/${encodeURIComponent(role)}`); setRepl({ role, ...data, loading: false }); }
    catch { setRepl(null); }
  };

  if (cc === null) return <Loader label="Loading command centre" />;
  if (!cc) return <Empty label="Tournament not found" />;
  const t = cc.tournament, tot = cc.totals;

  return (
    <div className="fade-up">
      <Link to="/app/ops" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-100 mb-4"><ArrowLeft className="w-4 h-4" /> Back to Krida Ops</Link>
      <PageHeader overline="Live event command centre" title={t.name}
        subtitle={`${t.venue} · Starts ${new Date(t.start_date).toLocaleDateString()}`}
        actions={<Tag tone="emerald"><Radio className="w-3 h-3 animate-pulse" /> Live</Tag>} testid="tournament-detail-page" />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard label="Required" value={tot.required} icon={Users} accent="slate" />
        <StatCard label="Confirmed" value={tot.confirmed} icon={CheckCircle2} accent="emerald" />
        <StatCard label="Checked in" value={tot.checked_in} icon={QrCode} accent="cobalt" testid="cc-checked-in" />
        <StatCard label="Active" value={tot.active} icon={Activity} accent="amber" />
        <StatCard label="Completed" value={tot.completed} icon={CheckCircle2} accent="emerald" />
        <StatCard label="Replacements" value={tot.replacement_needed} icon={AlertTriangle} accent="crimson" />
      </div>

      <Tabs defaultValue="workforce">
        <TabsList className="bg-krida-surface border border-white/10 mb-6">
          <TabsTrigger value="workforce" data-testid="tab-workforce" className="data-[state=active]:bg-krida-crimson data-[state=active]:text-white gap-2"><Users className="w-4 h-4" /> Workforce</TabsTrigger>
          <TabsTrigger value="roster" data-testid="tab-roster" className="data-[state=active]:bg-krida-crimson data-[state=active]:text-white gap-2"><ClipboardList className="w-4 h-4" /> Roster</TabsTrigger>
          <TabsTrigger value="requirements" data-testid="tab-requirements" className="data-[state=active]:bg-krida-crimson data-[state=active]:text-white gap-2"><Activity className="w-4 h-4" /> Requirements</TabsTrigger>
        </TabsList>

        <TabsContent value="workforce">
          {cc.credential_warnings.length > 0 && (
            <div className="krida-card p-4 mb-4 border-krida-amber/20">
              <div className="overline text-krida-amber mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Credential warnings</div>
              <div className="flex flex-wrap gap-2">{cc.credential_warnings.map((w, i) => <Tag key={i} tone="amber">{w.professional}: {w.credential} {w.status}</Tag>)}</div>
            </div>
          )}
          <div className="space-y-2">
            {cc.assignments.length === 0 && <Empty label="No workforce assigned yet" />}
            {cc.assignments.map((a) => (
              <div key={a.id} className="krida-card p-4 flex items-center justify-between" data-testid={`cc-assignment-${a.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-krida-cobalt/10 grid place-items-center font-heading font-bold text-krida-cobalt">{a.professional_name[0]}</div>
                  <div>
                    <div className="font-semibold">{a.professional_name}</div>
                    <div className="text-xs text-slate-500">{a.role} · {a.court || a.shift} · Report {a.reporting_time}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Tag tone={statusTone(a.status)}>{a.status.replace("_", " ")}</Tag>
                  {!a.checkin && ["confirmed", "accepted", "scheduled"].includes(a.status) && (
                    <button onClick={() => checkin(a.id)} data-testid={`checkin-btn-${a.id}`} className="px-3 py-1.5 rounded-lg bg-krida-emerald/15 text-krida-emerald text-xs font-semibold flex items-center gap-1 hover:bg-krida-emerald/25 transition-colors"><QrCode className="w-3.5 h-3.5" /> Check in</button>
                  )}
                  {a.checkin && <span className="text-xs text-krida-emerald flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> On time</span>}
                  {NEXT[a.status] && a.status !== "paid" && (
                    <button onClick={() => advance(a)} data-testid={`advance-btn-${a.id}`} className="px-3 py-1.5 rounded-lg bg-krida-crimson/15 text-krida-crimson text-xs font-semibold hover:bg-krida-crimson/25 transition-colors">Advance → {NEXT[a.status].replace("_", " ")}</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="roster">
          {roster === null ? <Loader /> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(roster.tournament.matches || []).length === 0 && <Empty label="No match roster defined" />}
              {(roster.tournament.matches || []).map((m, i) => (
                <div key={i} className="krida-card p-4" data-testid={`roster-match-${i}`}>
                  <div className="flex items-center justify-between"><span className="font-heading font-bold flex items-center gap-1.5"><MapPin className="w-4 h-4 text-krida-amber" /> {m.court}</span><Tag tone="cobalt">{m.time}</Tag></div>
                  <div className="text-sm font-semibold mt-2">{m.teams}</div>
                  <div className="flex flex-wrap gap-1.5 mt-3">{m.assigned.map((r, idx) => <Tag key={idx}>{r}</Tag>)}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="requirements">
          <div className="space-y-3">
            {cc.requirements.map((r, i) => {
              const pct = Math.round((r.confirmed / r.required) * 100);
              return (
                <div key={i} className="krida-card p-4" data-testid={`req-${r.role}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">{r.role}</span>
                    <div className="flex items-center gap-2">
                      {r.replacement_needed > 0 && (
                        <button onClick={() => findReplacements(r.role)} data-testid={`replace-btn-${r.role}`} className="px-3 py-1.5 rounded-lg bg-krida-crimson/15 text-krida-crimson text-xs font-semibold flex items-center gap-1 hover:bg-krida-crimson/25 transition-colors"><RefreshCw className="w-3 h-3" /> Find replacement</button>
                      )}
                      <span className="font-mono text-sm"><span className="text-krida-emerald">{r.confirmed}</span> / {r.required}</span>
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-krida-emerald to-krida-cobalt" style={{ width: `${pct}%` }} /></div>
                  <div className="flex gap-3 mt-2 text-xs text-slate-500">
                    <span>Invited {r.invited}</span><span>Checked in {r.checked_in}</span>
                    {r.replacement_needed > 0 && <span className="text-krida-crimson">Replacement needed {r.replacement_needed}</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {repl && (
            <div className="krida-card p-5 mt-4 border-krida-crimson/20">
              <div className="flex items-center gap-2 mb-3"><Zap className="w-4 h-4 text-krida-crimson" /><span className="font-heading font-bold">Replacement candidates — {repl.role}</span></div>
              {repl.loading ? <Loader /> : (
                <div className="space-y-2">
                  {(repl.replacements || []).map((r) => (
                    <div key={r.professional_id} className="flex items-center gap-3 p-2 rounded-lg bg-black/20">
                      <div className="w-10 h-10 rounded-lg grid place-items-center font-mono font-bold text-krida-emerald bg-krida-emerald/10">{r.score}%</div>
                      <img src={r.photo} alt="" className="w-9 h-9 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0"><div className="font-semibold text-sm">{r.name}</div><div className="text-xs text-slate-500 truncate">{r.reasons.slice(0, 2).join(" · ")}</div></div>
                      {r.eligible && <Tag tone="emerald">Ready</Tag>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
